"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PAGE_PRESETS = exports.PagePresetSchema = exports.PagePresetGeneratedChildRouteSchema = exports.PageSectionPresetSchema = void 0;
exports.getPagePreset = getPagePreset;
exports.getPagePresetForPage = getPagePresetForPage;
const zod_1 = require("zod");
const custom_app_config_1 = require("./custom-app-config");
const view_component_types_1 = require("./view-component-types");
exports.PageSectionPresetSchema = zod_1.z.object({
    componentType: zod_1.z
        .string()
        .min(1)
        .refine(view_component_types_1.isViewComponentType, value => ({
        message: `unknown view component type "${value}"`,
    })),
    sourceKeys: zod_1.z.array(zod_1.z.string().min(1)),
});
exports.PagePresetGeneratedChildRouteSchema = zod_1.z
    .object({
    title: zod_1.z.string().min(1),
    presetKey: zod_1.z.string().min(1),
    pathPattern: zod_1.z.string().min(1),
})
    .strict();
exports.PagePresetSchema = zod_1.z
    .object({
    presetKey: zod_1.z.string().min(1),
    routeKind: custom_app_config_1.RouteKindSchema,
    audience: custom_app_config_1.AudienceSchema,
    templateKey: zod_1.z.string().min(1),
    collectionRole: zod_1.z.string().min(1).nullable(),
    publicationModel: custom_app_config_1.PublicationModelSchema,
    requiredSourceKeys: zod_1.z.array(zod_1.z.string().min(1)),
    sourceContentModels: zod_1.z.record(zod_1.z.string().min(1), custom_app_config_1.CustomAppContentModelSchema),
    allowedComponentTypes: zod_1.z.array(zod_1.z.string().min(1)),
    defaultSections: zod_1.z.array(exports.PageSectionPresetSchema),
    generatedChildRoutes: zod_1.z
        .array(exports.PagePresetGeneratedChildRouteSchema)
        .optional(),
})
    .strict();
exports.PAGE_PRESETS = {
    landing: {
        presetKey: 'landing',
        routeKind: 'static',
        audience: 'public',
        templateKey: 'landing',
        collectionRole: null,
        publicationModel: 'evergreen',
        requiredSourceKeys: ['content'],
        sourceContentModels: { content: 'landing-content' },
        allowedComponentTypes: [
            'custom-app-header',
            'custom-app-navigation',
            'custom-app-footer',
            'hero-banner',
            'markdown-renderer',
            'card-grid',
            'article-list',
            'custom-html-block',
            'section',
            'grid-2',
            'grid-3',
            'kpi-card',
            'bar-chart',
            'line-chart',
            'form-view',
        ],
        defaultSections: [
            { componentType: 'custom-app-navigation', sourceKeys: ['navigation'] },
            { componentType: 'hero-banner', sourceKeys: ['content'] },
            { componentType: 'markdown-renderer', sourceKeys: ['content'] },
            { componentType: 'card-grid', sourceKeys: ['content'] },
            { componentType: 'custom-app-footer', sourceKeys: [] },
        ],
    },
    'legal-page': {
        presetKey: 'legal-page',
        routeKind: 'static',
        audience: 'public',
        templateKey: 'legal-page',
        collectionRole: 'content',
        publicationModel: 'evergreen',
        requiredSourceKeys: ['content'],
        sourceContentModels: { content: 'legal-page' },
        allowedComponentTypes: [
            'custom-app-header',
            'custom-app-navigation',
            'custom-app-footer',
            'single-article',
        ],
        defaultSections: [
            { componentType: 'custom-app-navigation', sourceKeys: ['navigation'] },
            { componentType: 'single-article', sourceKeys: ['content'] },
            { componentType: 'custom-app-footer', sourceKeys: [] },
        ],
    },
    'blog-index': {
        presetKey: 'blog-index',
        routeKind: 'index',
        audience: 'public',
        templateKey: 'blog-index',
        collectionRole: 'content',
        publicationModel: 'timestamped',
        requiredSourceKeys: ['users', 'content'],
        sourceContentModels: { users: 'custom-app-user', content: 'news-article' },
        allowedComponentTypes: [
            'custom-app-header',
            'custom-app-navigation',
            'custom-app-footer',
            'hero-banner',
            'article-list',
            'card-grid',
        ],
        defaultSections: [
            { componentType: 'custom-app-navigation', sourceKeys: ['navigation'] },
            { componentType: 'article-list', sourceKeys: ['content'] },
        ],
        generatedChildRoutes: [
            {
                title: 'Beitrag',
                presetKey: 'blog-post',
                pathPattern: ':slug',
            },
        ],
    },
    'blog-post': {
        presetKey: 'blog-post',
        routeKind: 'detail',
        audience: 'public',
        templateKey: 'blog-post',
        collectionRole: 'content',
        publicationModel: 'timestamped',
        requiredSourceKeys: ['users', 'content'],
        sourceContentModels: { users: 'custom-app-user', content: 'news-article' },
        allowedComponentTypes: [
            'custom-app-header',
            'hero-banner',
            'custom-app-navigation',
            'custom-app-footer',
            'single-article',
            'related-entries',
            'tag-display',
        ],
        defaultSections: [
            { componentType: 'custom-app-navigation', sourceKeys: ['navigation'] },
            { componentType: 'single-article', sourceKeys: ['content'] },
        ],
    },
    'knowledgebase-index': {
        presetKey: 'knowledgebase-index',
        routeKind: 'index',
        audience: 'public',
        templateKey: 'knowledgebase-index',
        collectionRole: 'content',
        publicationModel: 'evergreen',
        requiredSourceKeys: [
            'content',
            'users',
            'clusters',
            'topics',
            'articleBlocks',
        ],
        sourceContentModels: {
            users: 'custom-app-user',
            clusters: 'custom-app-cluster',
            topics: 'custom-app-topic',
            content: 'knowledge-base-entry',
            articleBlocks: 'kb-article-block',
        },
        allowedComponentTypes: [
            'custom-app-header',
            'custom-app-navigation',
            'custom-app-footer',
            'hero-banner',
            'knowledge-base-gallery',
        ],
        defaultSections: [
            { componentType: 'custom-app-navigation', sourceKeys: ['navigation'] },
            { componentType: 'knowledge-base-gallery', sourceKeys: ['content'] },
        ],
        generatedChildRoutes: [
            {
                title: 'Thema',
                presetKey: 'knowledgebase-topic',
                pathPattern: 'topics/:slug',
            },
            {
                title: 'Artikel',
                presetKey: 'knowledgebase-article',
                pathPattern: ':slug',
            },
        ],
    },
    'knowledgebase-topic': {
        presetKey: 'knowledgebase-topic',
        routeKind: 'detail',
        audience: 'public',
        templateKey: 'knowledgebase-topic',
        collectionRole: 'topics',
        publicationModel: 'evergreen',
        requiredSourceKeys: [
            'topics',
            'content',
            'users',
            'clusters',
            'articleBlocks',
        ],
        sourceContentModels: {
            users: 'custom-app-user',
            clusters: 'custom-app-cluster',
            topics: 'custom-app-topic',
            content: 'knowledge-base-entry',
            articleBlocks: 'kb-article-block',
        },
        allowedComponentTypes: [
            'custom-app-header',
            'custom-app-navigation',
            'custom-app-footer',
            'breadcrumb',
            'knowledge-base-gallery',
        ],
        defaultSections: [
            { componentType: 'custom-app-navigation', sourceKeys: ['navigation'] },
            { componentType: 'breadcrumb', sourceKeys: ['navigation'] },
            { componentType: 'knowledge-base-gallery', sourceKeys: ['content'] },
        ],
    },
    'knowledgebase-article': {
        presetKey: 'knowledgebase-article',
        routeKind: 'detail',
        audience: 'public',
        templateKey: 'knowledgebase-article',
        collectionRole: 'content',
        publicationModel: 'versioned',
        requiredSourceKeys: [
            'content',
            'users',
            'clusters',
            'topics',
            'articleBlocks',
        ],
        sourceContentModels: {
            users: 'custom-app-user',
            clusters: 'custom-app-cluster',
            topics: 'custom-app-topic',
            content: 'knowledge-base-entry',
            articleBlocks: 'kb-article-block',
        },
        allowedComponentTypes: [
            'custom-app-header',
            'custom-app-navigation',
            'custom-app-footer',
            'breadcrumb',
            'single-article',
            'related-entries',
        ],
        defaultSections: [
            { componentType: 'custom-app-navigation', sourceKeys: ['navigation'] },
            { componentType: 'breadcrumb', sourceKeys: ['navigation'] },
            { componentType: 'single-article', sourceKeys: ['content'] },
        ],
    },
    'intranet-dashboard': {
        presetKey: 'intranet-dashboard',
        routeKind: 'dashboard',
        audience: 'internal',
        templateKey: 'intranet-dashboard',
        collectionRole: 'content',
        publicationModel: 'evergreen',
        requiredSourceKeys: ['users', 'content'],
        sourceContentModels: {
            users: 'custom-app-user',
            content: 'intranet-dashboard-item',
        },
        allowedComponentTypes: [
            'custom-app-header',
            'kpi-card',
            'bar-chart',
            'line-chart',
            'custom-app-navigation',
            'custom-app-footer',
            'card-grid',
            'table-view',
            'filter-bar',
            'search-bar',
        ],
        defaultSections: [
            { componentType: 'custom-app-navigation', sourceKeys: ['navigation'] },
            { componentType: 'filter-bar', sourceKeys: ['content'] },
            { componentType: 'kpi-card', sourceKeys: ['content'] },
            { componentType: 'line-chart', sourceKeys: ['content'] },
            { componentType: 'bar-chart', sourceKeys: ['content'] },
            { componentType: 'table-view', sourceKeys: ['content'] },
        ],
    },
    'workflow-form': {
        presetKey: 'workflow-form',
        routeKind: 'form',
        audience: 'public',
        templateKey: 'workflow-form',
        collectionRole: 'forms',
        publicationModel: 'submission',
        requiredSourceKeys: ['forms', 'submissions'],
        sourceContentModels: {
            forms: 'workflow-form',
            submissions: 'workflow-submission',
        },
        allowedComponentTypes: [
            'custom-app-header',
            'custom-app-navigation',
            'custom-app-footer',
            'hero-banner',
            'form-view',
        ],
        defaultSections: [
            { componentType: 'custom-app-navigation', sourceKeys: ['navigation'] },
            { componentType: 'form-view', sourceKeys: ['forms'] },
        ],
    },
    custom: {
        presetKey: 'custom',
        routeKind: 'custom',
        audience: 'public',
        templateKey: 'custom',
        collectionRole: null,
        publicationModel: 'evergreen',
        requiredSourceKeys: [],
        sourceContentModels: {},
        allowedComponentTypes: [
            'custom-app-header',
            'hero-banner',
            'custom-app-navigation',
            'custom-app-footer',
            'card-grid',
            'article-list',
            'single-article',
            'table-view',
            'form-view',
        ],
        defaultSections: [],
    },
};
function getPagePreset(presetKey) {
    const preset = exports.PAGE_PRESETS[presetKey];
    return preset ? exports.PagePresetSchema.parse(preset) : null;
}
function getPagePresetForPage(page) {
    return getPagePreset(page.templateKey);
}
